import { API_PARAM_IS_REDESIGN_CONTEXT } from '../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../constants/common'
import { REQUEST_ENTITY } from '../constants/entity'
import { PAGINATION_ALL_ITEMS_SIZE } from '../constants/pagination'
import { ATTACH_SERVICE_REQUESTS_LOAD_ITEMS_GET_PARAMS } from '../constants/request'
import {
  createArrayParam,
  getApi,
  getId2IdEndpoint,
  patchApi,
  postApi,
  putApi,
} from '../utils/api'

function getRequestProfileEndpointBase({ id, outboundUuid }) {
  return outboundUuid
    ? `api/id2id/${outboundUuid}/request/v2/${id}`
    : `api/v2/requests/${id}`
}

function getRequestEndpointBase({ id, outboundUuid }) {
  return getId2IdEndpoint({ outboundUuid, path: `request/${id}` })
}

export function getAttachedServiceRequests({ excludeIds, params }) {
  const searchParams = new URLSearchParams([
    ...new URLSearchParams(params),
    ...(excludeIds ? createArrayParam('exclude_id', excludeIds) : EMPTY_LIST),
    ...ATTACH_SERVICE_REQUESTS_LOAD_ITEMS_GET_PARAMS,
  ])

  return getApi({
    endpoint: '/idwell-api/api/v3/request/request-options/',
    params: searchParams,
  })
}

export function createRequest(data) {
  return postApi({ data, endpoint: `idwell-api/api/v3/request/requests/` })
}

export function getRequestProfile({ id, outboundUuid, ...params }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/`,
    params: {
      expand_assignees: true,
      expand_labels: true,
      ...params,
    },
  })
}

export function getRequestProfileBase({ id, outboundUuid, params }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/`,
    params,
  })
}

export function getRequestProfileTabCounters({ id, outboundUuid, params }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/counters/`,
    params,
  })
}

export function getRequestProfileChatsCounters({ id, outboundUuid }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/chat-messages/count/`,
  })
}

export function updateRequestProfile({ data, id, outboundUuid }) {
  return patchApi({
    data,
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/`,
  })
}

export function getRequestPhase({ id, outboundUuid, params }) {
  return getApi({
    endpoint: `${getRequestEndpointBase({ id, outboundUuid })}/pipeline/`,
    params,
  })
}

export function sendRequestTaskEmail({ data, id, outboundUuid }) {
  return postApi({
    data,
    endpoint: `${getRequestEndpointBase({ id, outboundUuid })}/email-message/`,
  })
}

export function getRequestEmailThreads(
  id,
  params,
  { outboundUuid } = EMPTY_OBJECT
) {
  return getApi({
    endpoint: getId2IdEndpoint({
      outboundUuid,
      path: `${REQUEST_ENTITY.single}/${id}/email-thread/`,
    }),
    params: {
      ...params,
      [API_PARAM_IS_REDESIGN_CONTEXT]: 1,
      with_all_files: true, // seems we wont use api without this param
      with_all_recipients: true, // seems we wont use api without this param
    },
  })
}

export function getAllRequestEmailThreads(id, params) {
  return getApi({
    endpoint: `api/${REQUEST_ENTITY.single}/${id}/email-thread/all/`,
    params: {
      ...params,
      page_size: PAGINATION_ALL_ITEMS_SIZE,
    },
  })
}

export function getRequestsExtraInfo(params) {
  return getApi({
    endpoint: `idwell-api/api/v3/${REQUEST_ENTITY.single}/${REQUEST_ENTITY.plural}/extra-info/`,
    params,
  })
}

export function getRequestListLight(params) {
  return getApi({ endpoint: 'api/request-list-light/', params })
}

export function getRequestOptions(params) {
  return getApi({
    endpoint: 'idwell-api/api/v3/request/request-options/',
    params: { ...params, with_field: 'building' },
  })
}

export function getRequestRelatedRequestsCount(id, params) {
  return getApi({
    endpoint: `api/request-list-light/${id}/related-requests/count/`,
    params,
  })
}

export function getRequestArchivedPipelinesCount(id, params) {
  return getApi({
    endpoint: `api/request/${id}/archived-pipelines/count/`,
    params,
  })
}

export function getRequestArchivedPipelines(id, params) {
  return getApi({
    endpoint: `api/request/${id}/archived-pipelines/`,
    params,
  })
}

export function getAttachedRelatedServiceRequests({ excludeIds, id, params }) {
  const relatedRequestsParams = {
    ...params,
    related_request_id: id,
  }

  return getAttachedServiceRequests({
    excludeIds,
    params: relatedRequestsParams,
  })
}

export function attachRequest(id, data) {
  return postApi({ data, endpoint: `api/request/${id}/copy_messages/` })
}

export function copyFileToRelatedRequests(id, data) {
  return postApi({
    data,
    endpoint: `api/request-copy-file-to-related-requests/${id}/`,
  })
}

export function searchRequests({ apiConfig = EMPTY_OBJECT, search }) {
  return getApi({
    ...apiConfig,
    endpoint: 'api/request-list-light/',
    params: { ...apiConfig.params, search },
  })
}

export function searchArchivedRequests({ apiConfig = EMPTY_OBJECT, search }) {
  return getApi({
    ...apiConfig,
    endpoint: 'api/archived/request-list-light/',
    params: { ...apiConfig.params, search },
  })
}

export function getInhabitantPropertyInfo(id, params) {
  return getApi({
    endpoint: `idwell-api/api/v3/inhabitancy/inhabitants/${id}/`,
    params: {
      ...params,
      with_property_info: 1,
    },
  })
}

export function getWidgetMyServiceRequests(params) {
  return getApi({
    endpoint: 'idwell-api/api/v3/widget/widgets/today-requests/',
    params,
  })
}

export function getMassActionRequestsForConnect({ params, ...rest }) {
  const data = {
    ...params,
    ...rest,
  }

  return postApi({
    data,
    endpoint: 'idwell-api/api/v3/request/request-options/exclude-filter/',
  })
}

export function connectRequestsToRequest({ data, id, outboundUuid }) {
  return postApi({
    data,
    endpoint: `${getRequestEndpointBase({ id, outboundUuid })}/connect/`,
  })
}

export function disconnectRequests({ data, id, outboundUuid }) {
  return postApi({
    data,
    endpoint: `${getRequestEndpointBase({ id, outboundUuid })}/disconnect/`,
  })
}

export function updateConnectedRequests({ data, id }) {
  return postApi({
    data,
    endpoint: `api/request-list-light/${id}/related-requests/update/`,
  })
}

export function connectRequestDocFlows({ data, id }) {
  return postApi({
    data,
    endpoint: `api/v2/requests/${id}/connected-document-staplings/connect/`,
  })
}

export function disconnectRequestDocFlows({ data, id }) {
  return postApi({
    data,
    endpoint: `api/v2/requests/${id}/connected-document-staplings/disconnect/`,
  })
}

export function getRequestConnectedDocFlowsListApi(id) {
  return `idwell-api/api/v3/${REQUEST_ENTITY.single}/${REQUEST_ENTITY.plural}/${id}/request-connected-document-staplings/filter/`
}

export function getRequestConnectedDocFlows({ data, id }) {
  return postApi({ data, endpoint: getRequestConnectedDocFlowsListApi(id) })
}

export function loadRequestExportZipFieldsList() {
  return getApi({ endpoint: 'api/request-export-as-zip-files/fields-list/' })
}

export function getRequestProviders({ id, outboundUuid }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/providers/`,
  })
}

export function getRequestRecommendedContractors({ id, outboundUuid, params }) {
  return getApi({
    endpoint: `${getRequestProfileEndpointBase({ id, outboundUuid })}/recommended-contractors/`,
    params,
  })
}

export function getRequestRelatedRequestsRecipients(id, params) {
  return getApi({
    endpoint: `api/request-list-light/${id}/related-requests/recipients/`,
    params,
  })
}

export function createRelatedRequestsMassMailing(id, data) {
  return postApi({
    data,
    endpoint: `${getRequestEndpointBase({ id })}/related-requests-mass-mailing/`,
  })
}

export function performRequestMassAction(data) {
  return postApi({
    data,
    endpoint: `idwell-api/api/v3/${REQUEST_ENTITY.single}/${REQUEST_ENTITY.plural}/mass-action/`,
  })
}

export function performRequestAsyncMassAction(data) {
  return postApi({
    data,
    endpoint: `api/async-mass-action/${REQUEST_ENTITY.single}/`,
  })
}

export function updateRequestsTableConfig(
  data,
  entityPlural = REQUEST_ENTITY.plural
) {
  return putApi({
    data,
    endpoint: `idwell-api/api/v3/table/tables/${entityPlural}/config/`,
  })
}

export function updateRequest(data, id) {
  return patchApi({ data, endpoint: `api/request/${id}/` })
}

export function startRequestsExport(data) {
  return postApi({
    data,
    endpoint: 'idwell-api/api/v3/request/requests/export/',
  })
}

export function checkRequestExportTask(id) {
  return getApi({ endpoint: `api/request-export-task/${id}/` })
}

export function loadRequestExportFieldsList() {
  return getApi({ endpoint: 'api/request-export/fields-list/' })
}
